import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import { ShareModal } from '../../components/base/ShareModal';
import ShareToMessageModal, { SharedContent } from '../../components/base/ShareToMessageModal';
import { encodeSharedContent } from '../../components/base/SharedContentCard';
import { useMessages } from '../../hooks/useMessages';
import { Modal } from '../../components/base/Modal';
import { Button } from '../../components/base/Button';
import { Input } from '../../components/base/Input';
import TagInput from '../../components/base/TagInput';
import RichTextEditor from '../../components/base/RichTextEditor';
import ImageUpload from '../../components/base/ImageUpload';
import PostPreview from '../../components/base/PostPreview';
import PollResultsAnalytics from '../../components/base/PollResultsAnalytics';
import ForumBannerWidget from './components/ForumBannerWidget';
import { mockDiscussions, discussionCategories, getDiscussionsByCategory } from '../../mocks/discussions';
import { createDiscussionUrl } from '../../hooks/useNavigation';
import { createUserProfileUrl } from '../../hooks/useNavigation';
import CreatePostModal from './components/CreatePostModal';
import { useAuth } from '../../hooks/useAuth';
import { AuthModal } from '../../components/feature/AuthModal';
import { postsApi, activitiesApi, categoriesApi } from '../../lib/api';
import { useReputationRewards } from '../../hooks/useReputationRewards';
import { usePollVotes } from '../../hooks/usePollVotes';
import AIDramaApplyModal from '../../components/base/AIDramaApplyModal';

// Theme configurations for each category
const themeConfigs: Record<string, {
  gradient: string;
  accentColor: string;
  buttonBg: string;
  buttonHover: string;
  iconBg: string;
  borderColor: string;
  focusRing: string;
  tagBg: string;
  icon: string;
}> = {
  general: {
    gradient: 'from-blue-500 to-cyan-500',
    accentColor: 'text-blue-600',
    buttonBg: 'bg-blue-500',
    buttonHover: 'hover:bg-blue-600',
    iconBg: 'bg-blue-100',
    borderColor: 'border-blue-200',
    focusRing: 'focus:ring-blue-500',
    tagBg: 'bg-blue-50',
    icon: 'ri-chat-3-line'
  },
  'ai-dramas': {
    gradient: 'from-orange-500 to-amber-500',
    accentColor: 'text-orange-600',
    buttonBg: 'bg-orange-500',
    buttonHover: 'hover:bg-orange-600',
    iconBg: 'bg-orange-100',
    borderColor: 'border-orange-200',
    focusRing: 'focus:ring-orange-500',
    tagBg: 'bg-orange-50',
    icon: 'ri-robot-line'
  },
  reviews: {
    gradient: 'from-yellow-500 to-amber-500',
    accentColor: 'text-yellow-600',
    buttonBg: 'bg-yellow-500',
    buttonHover: 'hover:bg-yellow-600',
    iconBg: 'bg-yellow-100',
    borderColor: 'border-yellow-200',
    focusRing: 'focus:ring-yellow-500',
    tagBg: 'bg-yellow-50',
    icon: 'ri-star-line'
  },
  creators: {
    gradient: 'from-purple-500 to-pink-500',
    accentColor: 'text-purple-600',
    buttonBg: 'bg-purple-500',
    buttonHover: 'hover:bg-purple-600',
    iconBg: 'bg-purple-100',
    borderColor: 'border-purple-200',
    focusRing: 'focus:ring-purple-500',
    tagBg: 'bg-purple-50',
    icon: 'ri-user-star-line'
  },
  recommendations: {
    gradient: 'from-green-500 to-emerald-500',
    accentColor: 'text-green-600',
    buttonBg: 'bg-green-500',
    buttonHover: 'hover:bg-green-600',
    iconBg: 'bg-green-100',
    borderColor: 'border-green-200',
    focusRing: 'focus:ring-green-500',
    tagBg: 'bg-green-50',
    icon: 'ri-thumb-up-line'
  },
  news: {
    gradient: 'from-red-500 to-rose-500',
    accentColor: 'text-red-600',
    buttonBg: 'bg-red-500',
    buttonHover: 'hover:bg-red-600',
    iconBg: 'bg-red-100',
    borderColor: 'border-red-200',
    focusRing: 'focus:ring-red-500',
    tagBg: 'bg-red-50',
    icon: 'ri-megaphone-line'
  },
  'behind-scenes': {
    gradient: 'from-indigo-500 to-violet-500',
    accentColor: 'text-indigo-600',
    buttonBg: 'bg-indigo-500',
    buttonHover: 'hover:bg-indigo-600',
    iconBg: 'bg-indigo-100',
    borderColor: 'border-indigo-200',
    focusRing: 'focus:ring-indigo-500',
    tagBg: 'bg-indigo-50',
    icon: 'ri-camera-line'
  }
};

// Write Review Modal - Drama Reviews Theme
function WriteReviewModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
  const theme = themeConfigs.reviews;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [rating, setRating] = useState(0);
  const [drama, setDrama] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = drama && title && content && rating > 0;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to create a review');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: 'c1e1fc24-0748-4621-b607-9ad15eab0cea', // Drama Reviews UUID
        author_id: user.id,
        tags: [...tags, `rating-${rating}`, drama.toLowerCase().replace(/\s+/g, '-')],
        images: images.length > 0 ? images : null,
        metadata: { drama, rating, type: 'review' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create review');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'review_created',
        title: 'Wrote a drama review',
        description: `${drama}: ${title}`,
        data: { post_id: response.data?.id, rating }
      }).catch(console.warn);

      showNotification('Review submitted successfully!', 'success');
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setRating(0); setDrama(''); setTags([]); setImages([]); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Write a Review</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your review before publishing' : 'Share your thoughts and ratings with the community'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://public.readdy.ai/ai/img_res/d09dc03e17eadcecb0c991545a860872.jpg'
            }}
            category="Drama Reviews"
            categoryColor="bg-yellow-500"
            categoryIcon="ri-star-line"
            tags={tags}
            images={images}
            metadata={{ drama, rating }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Submit Review"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-film-line mr-1"></i> Drama Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={drama} 
                onChange={(e) => setDrama(e.target.value)} 
                placeholder="Which drama are you reviewing?" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-star-line mr-1"></i> Your Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                {[1,2,3,4,5].map(star => (
                  <button 
                    key={star} 
                    type="button" 
                    onClick={() => setRating(star)} 
                    className="cursor-pointer transition-transform hover:scale-110"
                  >
                    <i className={`ri-star-${star <= rating ? 'fill' : 'line'} text-3xl text-yellow-400`}></i>
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-3 text-sm font-medium text-yellow-700">
                    {rating === 5 ? 'Masterpiece!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-edit-line mr-1"></i> Review Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Give your review a catchy title" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> Your Review <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share your detailed thoughts about the drama... What did you love? What could be better?"
                className="border-yellow-200 focus-within:ring-2 focus-within:ring-yellow-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add screenshots or images to enhance your review</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'emotional', 'plot-twist', 'must-watch'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview Review
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// Create Recommendation Modal - Recommendations Theme
function CreateRecommendationModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs.recommendations;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [dramas, setDramas] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = title && content && dramas.length > 0;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to create a recommendation');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: '95cb1676-240b-4de9-b97b-8b5e118b98bd', // Recommendations UUID
        author_id: user.id,
        tags: tags,
        images: images.length > 0 ? images : null,
        metadata: { recommendedDramas: dramas, type: 'recommendation' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create recommendation');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'recommendation_created',
        title: 'Shared a recommendation',
        description: title,
        data: { post_id: response.data?.id, dramas }
      }).catch(console.warn);

      showNotification('Recommendation shared successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to share recommendation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setDramas([]); setTags([]); setImages([]); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Share a Recommendation</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your recommendation before publishing' : 'Help others discover amazing dramas'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="Recommendations"
            categoryColor="bg-green-500"
            categoryIcon="ri-thumb-up-line"
            tags={tags}
            images={images}
            metadata={{ recommendedDramas: dramas }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Share Recommendation"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-lightbulb-line mr-1"></i> Recommendation Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="e.g., 'Best Romantic Dramas for a Cozy Weekend'" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-movie-2-line mr-1"></i> Recommended Dramas <span className="text-red-500">*</span>
              </label>
              <div className={`p-3 ${theme.tagBg} rounded-lg border ${theme.borderColor}`}>
                <TagInput
                  tags={dramas}
                  onChange={setDramas}
                  placeholder="Add drama titles you're recommending..."
                  maxTags={10}
                />
                <p className="text-xs text-green-600 mt-2">
                  <i className="ri-information-line mr-1"></i>
                  Add up to 10 dramas you want to recommend
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> Why Do You Recommend These? <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share why others should watch these dramas... What makes them special? Who would enjoy them?"
                className="border-green-200 focus-within:ring-2 focus-within:ring-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add drama posters or screenshots</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Category Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'romance', 'thriller', 'family-friendly'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// Ask Recommendation Modal - Recommendations Theme (Alternate Style)
function AskRecommendationModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs.recommendations;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [preferences, setPreferences] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = title && content;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to ask for recommendations');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: '95cb1676-240b-4de9-b97b-8b5e118b98bd', // Recommendations UUID
        author_id: user.id,
        tags: [...preferences, 'looking-for-recommendations'],
        images: null,
        metadata: { preferences, type: 'ask_recommendation' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to post question');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'question_posted',
        title: 'Asked for recommendations',
        description: title,
        data: { post_id: response.data?.id }
      }).catch(console.warn);

      showNotification('Question posted successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to post question');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setPreferences([]); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className=" -mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <i className="ri-question-line text-xl text-teal-600"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Ask for Recommendations</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your question before posting' : 'Let the community suggest your next binge!'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="Recommendations"
            categoryColor="bg-teal-500"
            categoryIcon="ri-question-line"
            tags={[...preferences, 'looking-for-recommendations']}
            images={[]}
            metadata={{}}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Ask Community"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-search-line mr-1"></i> What Are You Looking For? <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="e.g., 'Romantic dramas with happy endings'" 
                className="focus:ring-teal-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-heart-line mr-1"></i> Your Preferences (Optional)
              </label>
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <TagInput
                  tags={preferences}
                  onChange={setPreferences}
                  placeholder="Add preferences like 'no sad endings', 'strong female lead'..."
                  maxTags={8}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-file-text-line mr-1"></i> More Details <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Describe what you enjoy watching... Any specific themes, settings, or vibes?"
                className="border-teal-200 focus-within:ring-2 focus-within:ring-teal-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className="bg-teal-500 hover:bg-teal-600 text-white whitespace-nowrap"
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// Submit News Modal - Industry News Theme
function SubmitNewsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs.news;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [source, setSource] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [isBreaking, setIsBreaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = title && content;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to submit news');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: 'c21c6283-d0f3-42ce-bb3a-a46ac9381b81', // Industry News UUID
        author_id: user.id,
        tags: isBreaking ? [...tags, 'breaking-news'] : tags,
        images: images.length > 0 ? images : null,
        metadata: { source, isBreaking, type: 'news' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to submit news');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'news_submitted',
        title: 'Submitted industry news',
        description: title,
        data: { post_id: response.data?.id, isBreaking }
      }).catch(console.warn);

      showNotification('News submitted successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit news');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setSource(''); setTags([]); setImages([]); setIsBreaking(false); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Submit News</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your news before publishing' : 'Share the latest industry updates with the community'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="Industry News"
            categoryColor="bg-red-500"
            categoryIcon="ri-megaphone-line"
            tags={isBreaking ? [...tags, 'breaking-news'] : tags}
            images={images}
            metadata={{ source, isBreaking }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Submit News"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isBreaking}
                  onChange={(e) => setIsBreaking(e.target.checked)}
                  className="w-4 h-4 text-red-500 rounded focus:ring-red-500"
                />
                <span className="text-sm font-medium text-red-700">
                  <i className="ri-alarm-warning-line mr-1"></i>
                  Mark as Breaking News
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-newspaper-line mr-1"></i> Headline <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Write a compelling news headline" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-links-line mr-1"></i> Source (Optional)
              </label>
              <Input 
                value={source} 
                onChange={(e) => setSource(e.target.value)} 
                placeholder="Where did you find this news? (e.g., Variety, Official Announcement)" 
                className={`${theme.focusRing}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-article-line mr-1"></i> News Details <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share the full news story... Include quotes, context, and any relevant details."
                className="border-red-200 focus-within:ring-2 focus-within:ring-red-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add relevant images or photos</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'awards', 'casting', 'production'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// Share Story Modal - Behind the Scenes Theme
function ShareStoryModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs['behind-scenes'];
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [drama, setDrama] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [storyType, setStoryType] = useState('');
  const [mood, setMood] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollSettings, setPollSettings] = useState({
    allowMultiple: false,
    showResults: true,
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const storyTypes = [
    { value: 'filming', label: 'Filming Story', icon: 'ri-film-line' },
    { value: 'production', label: 'Production Insight', icon: 'ri-settings-3-line' },
    { value: 'cast', label: 'Cast & Crew', icon: 'ri-group-line' },
    { value: 'technical', label: 'Technical Details', icon: 'ri-tools-line' },
    { value: 'other', label: 'Other', icon: 'ri-more-line' }
  ];

  const moodOptions = [
    { value: 'excited', label: 'Excited', emoji: '🎉' },
    { value: 'curious', label: 'Curious', emoji: '🤔' },
    { value: 'thoughtful', label: 'Thoughtful', emoji: '💭' },
    { value: 'happy', label: 'Happy', emoji: '😊' },
    { value: 'nostalgic', label: 'Nostalgic', emoji: '🥹' }
  ];

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = drama && title && content;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to share a story');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const allTags = [...tags, storyType];
      if (mood) allTags.push(mood);

      // Prepare poll data if it's a poll type
      const pollData = storyType === 'poll' ? {
        options: pollOptions.map((text, index) => ({
          id: `option-${index}`,
          text,
          votes: 0
        })),
        allowMultiple: pollSettings.allowMultiple,
        showResults: pollSettings.showResults,
        endDate: pollSettings.endDate || null,
        totalVotes: 0
      } : null;

      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: '84cfe33d-f9f1-4ab1-a7e1-0a352897b286', // Behind the Scenes UUID
        author_id: user.id,
        tags: allTags,
        images: images.length > 0 ? images : null,
        metadata: { 
          drama, 
          storyType, 
          type: 'behind_scenes',
          poll: pollData
        },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to share story');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'story_shared',
        title: 'Shared a behind-the-scenes story',
        description: `${drama}: ${title}`,
        data: { post_id: response.data?.id, storyType }
      }).catch(console.warn);

      showNotification('Story shared successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to share story');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setDrama(''); setTags([]); setImages([]); setStoryType(''); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Share Behind-the-Scenes Story</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your story before publishing' : 'Give fans an exclusive look at the production process'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="Behind the Scenes"
            categoryColor="bg-indigo-500"
            categoryIcon="ri-camera-line"
            tags={storyType ? [...tags, storyType] : tags}
            images={images}
            metadata={{ drama, storyType }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Share Story"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-movie-2-line mr-1"></i> Related Drama <span className="text-red-500">*</span>
              </label>
              <Input 
                value={drama} 
                onChange={(e) => setDrama(e.target.value)} 
                placeholder="Which drama is this story about?" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-folder-line mr-1"></i> Story Type
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {storyTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setStoryType(type.value)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${storyType === type.value 
                        ? `${theme.borderColor} ${theme.tagBg}` 
                        : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${type.icon} text-lg`}></i>
                      <span className="text-sm font-medium">{type.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-edit-line mr-1"></i> Story Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Give your story an intriguing title" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> Your Story <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share the behind-the-scenes details... What happened during production? Any interesting anecdotes?"
                className="border-indigo-200 focus-within:ring-2 focus-within:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add behind-the-scenes photos or production images</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'filming', 'costume-design', 'vfx'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// AI Dramas Post Modal - AI Dramas Theme
function CreateAIDramaPostModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs['ai-dramas'];
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [postType, setPostType] = useState('');
  const [tools, setTools] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const postTypes = [
    { value: 'showcase', label: 'Showcase', icon: 'ri-gallery-line', desc: 'Share your AI creation' },
    { value: 'tutorial', label: 'Tutorial', icon: 'ri-book-open-line', desc: 'Teach a technique' },
    { value: 'discussion', label: 'Discussion', icon: 'ri-discuss-line', desc: 'Start a conversation' },
    { value: 'question', label: 'Question', icon: 'ri-question-line', desc: 'Ask for help' },
    { value: 'review', label: 'Tool Review', icon: 'ri-star-line', desc: 'Review an AI tool' }
  ];

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = title && content && postType;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to create a post');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: '39a97261-dd83-42b7-9c6a-292161ae0803', // AI Dramas UUID
        author_id: user.id,
        tags: [...tags, postType],
        images: images.length > 0 ? images : null,
        metadata: { postType, tools, type: 'ai_drama' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create post');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'ai_post_created',
        title: 'Created an AI drama post',
        description: title,
        data: { post_id: response.data?.id, postType, tools }
      }).catch(console.warn);

      showNotification('Post created successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setTags([]); setImages([]); setPostType(''); setTools([]); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Create AI Drama Post</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your post before publishing' : 'Share your AI creations, tutorials, or questions'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="AI Dramas"
            categoryColor="bg-orange-500"
            categoryIcon="ri-robot-line"
            tags={[...tags, postType]}
            images={images}
            metadata={{ postType, tools }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Create Post"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-folder-line mr-1"></i> Post Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {postTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setPostType(type.value)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${postType === type.value 
                        ? `${theme.borderColor} ${theme.tagBg}` 
                        : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${type.icon} text-lg ${postType === type.value ? theme.accentColor : 'text-gray-500'}`}></i>
                      <span className={`text-sm font-medium ${postType === type.value ? theme.accentColor : 'text-gray-700'}`}>{type.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-edit-line mr-1"></i> Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Give your post an engaging title" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-tools-line mr-1"></i> AI Tools Used (Optional)
              </label>
              <div className={`p-3 ${theme.tagBg} rounded-lg border ${theme.borderColor}`}>
                <TagInput
                  tags={tools}
                  onChange={setTools}
                  placeholder="Add tools like 'Sora', 'Runway', 'ElevenLabs'..."
                  maxTags={8}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> Content <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share your AI drama content, tutorial steps, or questions..."
                className={`border-orange-200 focus-within:ring-2 focus-within:ring-orange-500`}
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add screenshots, AI-generated images, or examples</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'tutorial', 'showcase', 'character-consistency'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// Creator Spotlight Post
function CreateCreatorPostModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs.creators;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [creatorRole, setCreatorRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const creatorRoles = [
    { value: 'actor', label: 'Actor/Actress', icon: 'ri-user-star-line' },
    { value: 'director', label: 'Director', icon: 'ri-movie-line' },
    { value: 'writer', label: 'Writer', icon: 'ri-quill-pen-line' },
    { value: 'producer', label: 'Producer', icon: 'ri-briefcase-line' },
    { value: 'composer', label: 'Composer', icon: 'ri-music-line' },
    { value: 'other', label: 'Other', icon: 'ri-more-line' }
  ];

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const canPreview = creatorName && title && content;

  const handlePreview = () => {
    if (!canPreview) {
      setError('Please fill in all required fields before previewing');
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to create a post');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: 'a0d5eb16-9fbb-4a82-b79a-3aa2135521cd', // Creator Spotlight UUID
        author_id: user.id,
        tags: creatorRole ? [...tags, creatorRole] : tags,
        images: images.length > 0 ? images : null,
        metadata: { creatorName, creatorRole, type: 'creator_spotlight' },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create post');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: 'spotlight_created',
        title: 'Created a creator spotlight',
        description: `${creatorName}: ${title}`,
        data: { post_id: response.data?.id, creatorRole }
      }).catch(console.warn);

      showNotification('Post created successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setCreatorName(''); setTags([]); setImages([]); setCreatorRole(''); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Creator Spotlight Post</h2>
              <p className="text-white/80 text-sm">
                {showPreview ? 'Preview your post before publishing' : 'Celebrate and discuss talented creators'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="Creator Spotlight"
            categoryColor="bg-purple-500"
            categoryIcon="ri-user-star-line"
            tags={creatorRole ? [...tags, creatorRole] : tags}
            images={images}
            metadata={{ creatorName, creatorRole }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="Create Post"
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-user-star-line mr-1"></i> Creator Name <span className="text-red-500">*</span>
              </label>
              <Input 
                value={creatorName} 
                onChange={(e) => setCreatorName(e.target.value)} 
                placeholder="Who is this post about?" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-briefcase-line mr-1"></i> Creator Role
              </label>
              <div className="grid grid-cols-3 gap-2">
                {creatorRoles.map(role => (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => setCreatorRole(role.value)}
                    className={`p-2 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-2 ${creatorRole === role.value 
                        ? `${theme.borderColor} ${theme.tagBg} ${theme.accentColor}` 
                        : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <i className={`${role.icon}`}></i>
                    <span className="text-sm">{role.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-edit-line mr-1"></i> Post Title <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="Give your post a title" 
                className={`${theme.focusRing}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> Your Post <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder="Share insights about this creator... Their work, achievements, or why you admire them."
                className={`border-purple-200 focus-within:ring-2 focus-within:ring-purple-500`}
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-image-line mr-1"></i> Images (Optional)
              </label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
                maxFiles={5}
                maxSize={2 * 1024 * 1024}
                uploadedUrls={images}
              />
              <p className="text-xs text-gray-500 mt-1">Add photos of the creator or their work</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'interview', 'career', 'rising-star'..."
                maxTags={5}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

// General Discussion Post Modal
function GeneralDiscussionModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const theme = themeConfigs.general;
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [discussionType, setDiscussionType] = useState('');
  const [mood, setMood] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollSettings, setPollSettings] = useState({
    allowMultiple: false,
    showResults: true,
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const discussionTypes = [
    { value: 'discussion', label: 'Discussion', icon: 'ri-discuss-line', desc: 'Start a conversation' },
    { value: 'question', label: 'Question', icon: 'ri-question-line', desc: 'Ask the community' },
    { value: 'opinion', label: 'Opinion', icon: 'ri-chat-quote-line', desc: 'Share your thoughts' },
    { value: 'poll', label: 'Poll', icon: 'ri-bar-chart-horizontal-line', desc: 'Get community votes' },
    { value: 'announcement', label: 'Announcement', icon: 'ri-megaphone-line', desc: 'Share news' }
  ];

  const moodOptions = [
    { value: 'excited', label: 'Excited', emoji: '🎉' },
    { value: 'curious', label: 'Curious', emoji: '🤔' },
    { value: 'thoughtful', label: 'Thoughtful', emoji: '💭' },
    { value: 'happy', label: 'Happy', emoji: '😊' },
    { value: 'nostalgic', label: 'Nostalgic', emoji: '🥹' }
  ];

  const handleContentChange = (text: string, html?: string) => {
    setContent(text);
    setContentHtml(html || text);
  };

  const handleImageUploaded = (url: string) => {
    setImages(prev => [...prev, url]);
  };

  const handleImageRemoved = (url: string) => {
    setImages(prev => prev.filter(img => img !== url));
  };

  // Poll option handlers
  const handlePollOptionChange = (index: number, value: string) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const addPollOption = () => {
    if (pollOptions.length < 10) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      const newOptions = pollOptions.filter((_, i) => i !== index);
      setPollOptions(newOptions);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${type === 'success' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="ri-${type === 'success' ? 'check-circle' : 'error-warning'}-line mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  // Validate poll options
  const validPollOptions = pollOptions.filter(opt => opt.trim() !== '');
  const isPollValid = discussionType !== 'poll' || validPollOptions.length >= 2;

  const canPreview = title && content && discussionType && isPollValid;

  const handlePreview = () => {
    if (!canPreview) {
      if (discussionType === 'poll' && validPollOptions.length < 2) {
        setError('Please add at least 2 poll options');
      } else {
        setError('Please fill in all required fields before previewing');
      }
      return;
    }
    setError('');
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to create a post');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const allTags = [...tags, discussionType];
      if (mood) allTags.push(mood);

      // Prepare poll data if it's a poll type
      const pollData = discussionType === 'poll' ? {
        options: validPollOptions.map((text, index) => ({
          id: `option-${index}`,
          text,
          votes: 0
        })),
        allowMultiple: pollSettings.allowMultiple,
        showResults: pollSettings.showResults,
        endDate: pollSettings.endDate || null,
        totalVotes: 0
      } : null;

      const response = await postsApi.createPost({
        title: title.trim(),
        content: content.trim(),
        content_html: contentHtml || content.trim(),
        category_id: '18271060-2b2a-4d3c-92e7-02839d5ab99c', // General Discussion UUID
        author_id: user.id,
        tags: allTags,
        images: images.length > 0 ? images : null,
        metadata: { 
          discussionType, 
          mood, 
          type: 'general_discussion',
          poll: pollData
        },
        status: 'pending',
        upvotes: 0,
        downvotes: 0
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create post');
      }

      await awardPoints(user.id, 'create_post');
      activitiesApi.createActivity({
        activityType: discussionType === 'poll' ? 'poll_created' : 'post_created',
        title: discussionType === 'poll' ? 'Created a poll' : 'Started a discussion',
        description: title,
        data: { post_id: response.data?.id, discussionType }
      }).catch(console.warn);

      showNotification(discussionType === 'poll' ? 'Poll created successfully!' : 'Post created successfully!', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle(''); setContent(''); setContentHtml(''); setTags([]); setImages([]); setDiscussionType(''); setMood(''); setPollOptions(['', '']); setPollSettings({ allowMultiple: false, showResults: true, endDate: '' }); setError(''); setShowPreview(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Custom Header */}
        <div className={`-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r ${theme.gradient} rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${theme.iconBg} rounded-xl flex items-center justify-center`}>
              <i className={`${discussionType === 'poll' ? 'ri-bar-chart-horizontal-line' : theme.icon} text-xl ${theme.accentColor}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {discussionType === 'poll' ? 'Create a Poll' : 'Start a Discussion'}
              </h2>
              <p className="text-white/80 text-sm">
                {showPreview 
                  ? 'Preview your post before publishing' 
                  : discussionType === 'poll' 
                    ? 'Let the community vote on your question'
                    : 'Share your thoughts with the community'}
              </p>
            </div>
          </div>
        </div>

        {showPreview ? (
          <PostPreview
            title={title}
            content={content}
            contentHtml={contentHtml}
            author={{
              username: user?.user_metadata?.username || user?.email?.split('@')[0] || 'You',
              avatar: user?.user_metadata?.avatar_url || 'https://readdy.ai/api/search-image?query=default%20user%20avatar%20minimal%20style&width=100&height=100&seq=1&orientation=squarish'
            }}
            category="General Discussion"
            categoryColor="bg-blue-500"
            categoryIcon={discussionType === 'poll' ? 'ri-bar-chart-horizontal-line' : 'ri-chat-3-line'}
            tags={[...tags, discussionType, ...(mood ? [mood] : [])]}
            images={images}
            metadata={{ 
              discussionType, 
              mood,
              poll: discussionType === 'poll' ? {
                options: validPollOptions.map((text, index) => ({
                  id: `option-${index}`,
                  text,
                  votes: 0
                })),
                allowMultiple: pollSettings.allowMultiple,
                showResults: pollSettings.showResults,
                endDate: pollSettings.endDate || null,
                totalVotes: 0
              } : undefined
            }}
            onBack={() => setShowPreview(false)}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText={discussionType === 'poll' ? 'Create Poll' : 'Publish Post'}
          />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePreview(); }} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-2"></i>{error}
              </div>
            )}

            {/* Discussion Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-folder-line mr-1"></i> Post Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {discussionTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setDiscussionType(type.value)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${discussionType === type.value 
                        ? `${theme.borderColor} ${theme.tagBg}` 
                        : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${type.icon} text-lg ${discussionType === type.value ? theme.accentColor : 'text-gray-500'}`}></i>
                      <span className={`text-sm font-medium ${discussionType === type.value ? theme.accentColor : 'text-gray-700'}`}>{type.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-edit-line mr-1"></i> {discussionType === 'poll' ? 'Poll Question' : 'Title'} <span className="text-red-500">*</span>
              </label>
              <Input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder={
                  discussionType === 'question' ? "What would you like to ask the community?" :
                  discussionType === 'opinion' ? "What's your take on..." :
                  discussionType === 'poll' ? "What do you want the community to vote on?" :
                  discussionType === 'announcement' ? "What's the announcement?" :
                  "Give your post an engaging title"
                }
                className={`${theme.focusRing}`}
                required
              />
            </div>

            {/* Poll Options - Only show when poll type is selected */}
            {discussionType === 'poll' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-list-check mr-1"></i> Poll Options <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-2">(min 2, max 10)</span>
                </label>
                <div className="space-y-2">
                  {pollOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium flex-shrink-0">
                        {index + 1}
                      </div>
                      <Input
                        value={option}
                        onChange={(e) => handlePollOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                        maxLength={100}
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removePollOption(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {pollOptions.length < 10 && (
                  <button
                    type="button"
                    onClick={addPollOption}
                    className="mt-3 flex items-center gap-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-add-circle-line"></i>
                    Add another option
                  </button>
                )}

                {/* Poll Settings */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-800 mb-3">
                    <i className="ri-settings-3-line mr-1"></i> Poll Settings
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pollSettings.allowMultiple}
                        onChange={(e) => setPollSettings(prev => ({ ...prev, allowMultiple: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Allow multiple selections</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pollSettings.showResults}
                        onChange={(e) => setPollSettings(prev => ({ ...prev, showResults: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show results before voting</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">End date (optional):</span>
                      <input
                        type="date"
                        value={pollSettings.endDate}
                        onChange={(e) => setPollSettings(prev => ({ ...prev, endDate: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-quill-pen-line mr-1"></i> {discussionType === 'poll' ? 'Additional Context (Optional)' : 'Content'} {discussionType !== 'poll' && <span className="text-red-500">*</span>}
              </label>
              <RichTextEditor
                value={content}
                onChange={handleContentChange}
                placeholder={
                  discussionType === 'question' ? "Provide more context about your question..." :
                  discussionType === 'opinion' ? "Share your detailed thoughts and reasoning..." :
                  discussionType === 'poll' ? "Add any additional context or explanation for your poll..." :
                  "Share your thoughts, ideas, or start a conversation..."
                }
                className="border-blue-200 focus-within:ring-2 focus-within:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{content.length}/5000 characters</p>
            </div>

            {/* Mood Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-emotion-line mr-1"></i> How are you feeling? (Optional)
              </label>
              <div className="flex flex-wrap gap-2">
                {moodOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMood(mood === option.value ? '' : option.value)}
                    className={`px-3 py-2 rounded-full border-2 transition-all cursor-pointer flex items-center gap-2 ${mood === option.value 
                        ? `${theme.borderColor} ${theme.tagBg} ${theme.accentColor}` 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                  >
                    <span>{option.emoji}</span>
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Images - Hide for polls */}
            {discussionType !== 'poll' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-image-line mr-1"></i> Images (Optional)
                </label>
                <ImageUpload
                  onImageUploaded={handleImageUploaded}
                  onImageRemoved={handleImageRemoved}
                  maxFiles={5}
                  maxSize={2 * 1024 * 1024}
                  uploadedUrls={images}
                />
                <p className="text-xs text-gray-500 mt-1">Add images to make your post more engaging</p>
              </div>
            )}

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> Tags (Optional)
              </label>
              <TagInput
                tags={tags}
                onChange={setTags}
                placeholder="Add tags like 'favorites', 'discussion', 'recommendations'..."
                maxTags={5}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} className="whitespace-nowrap">Cancel</Button>
              <Button 
                type="submit" 
                disabled={!canPreview}
                className={`${theme.buttonBg} ${theme.buttonHover} text-white whitespace-nowrap`}
              >
                <i className="ri-eye-line mr-2"></i>Preview {discussionType === 'poll' ? 'Poll' : 'Post'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

export default function ForumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [sortBy, setSortBy] = useState('latest');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Auth Modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Modals
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [showCreateRecommendation, setShowCreateRecommendation] = useState(false);
  const [showAskRecommendation, setShowAskRecommendation] = useState(false);
  const [showSubmitNews, setShowSubmitNews] = useState(false);
  const [showShareStory, setShowShareStory] = useState(false);
  const [showAIDramaPost, setShowAIDramaPost] = useState(false);
  const [showCreatorPost, setShowCreatorPost] = useState(false);
  const [showGeneralDiscussion, setShowGeneralDiscussion] = useState(false);
  const [sharePost, setSharePost] = useState<any>(null);
  const [shareToMessageContent, setShareToMessageContent] = useState<SharedContent | null>(null);
  const [showShareToMessage, setShowShareToMessage] = useState(false);
  const [showAIDramaApply, setShowAIDramaApply] = useState(false);

  // Post interactions state
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());

  // Poll analytics state
  const [showPollAnalytics, setShowPollAnalytics] = useState(false);
  const [selectedPollData, setSelectedPollData] = useState<any>(null);

  // Poll share state
  const [sharePollResults, setSharePollResults] = useState<any>(null);

  // Poll voting with database persistence
  const {
    votedPolls,
    submittedPolls,
    loading: pollVotesLoading,
    realtimeVoteCounts,
    isPollExpired,
    handleSingleVote,
    toggleMultiOption,
    submitMultiVote,
    fetchVoteCounts,
  } = usePollVotes();

  const { sendMessage, startConversation, fetchConversations } = useMessages();

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && discussionCategories.some(c => c.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams(tabId === 'general' ? {} : { tab: tabId });
  };

  const currentCategory = discussionCategories.find(c => c.id === activeTab) || discussionCategories[0];

  // Get posts for current category from mock data
  const posts = useMemo(() => {
    return getDiscussionsByCategory(activeTab);
  }, [activeTab]);

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    let filtered = posts.filter(post =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    switch (sortBy) {
      case 'popular':
        filtered.sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes));
        break;
      case 'discussed':
        filtered.sort((a, b) => b.commentCount - a.commentCount);
        break;
      case 'views':
        filtered.sort((a, b) => b.views - a.views);
        break;
      default:
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    return filtered;
  }, [posts, searchTerm, sortBy]);

  const categoryStats: Record<string, { posts: number; members: number; extra: string }> = {
    general: { posts: 1247, members: 8934, extra: 'Active community' },
    reviews: { posts: 892, members: 5621, extra: '4.6 avg rating' },
    creators: { posts: 634, members: 4387, extra: 'Featured creators' },
    recommendations: { posts: 756, members: 6892, extra: 'Curated picks' },
    news: { posts: 423, members: 3456, extra: 'Updated daily' },
    'behind-scenes': { posts: 298, members: 2134, extra: 'Exclusive content' },
    'ai-dramas': { posts: 512, members: 7234, extra: 'Trending now' },
  };

  const sidebarContent: Record<string, { about: string; tags: string[] }> = {
    general: {
      about: 'A welcoming space for open discussions about Arab dramas, sharing thoughts, opinions, and engaging with fellow drama enthusiasts.',
      tags: ['discussion', 'storytelling', 'culture', 'recommendations', 'favorites', '2024', 'characters', 'plot-twists'],
    },
    reviews: {
      about: 'Share your thoughts and detailed critiques of Arab short dramas. Help fellow viewers discover great content through honest, thoughtful reviews.',
      tags: ['noor', 'desert-winds', 'emotional', 'season-finale', 'character-development', 'city-of-dreams'],
    },
    creators: {
      about: 'Celebrate and discuss the talented individuals who bring Arab dramas to life. Share insights about actors, directors, producers, and other creative professionals.',
      tags: ['layla-al-rashid', 'ahmed-hassan', 'directing', 'acting', 'rising-stars', 'cinematography'],
    },
    recommendations: {
      about: 'Discover new dramas through community recommendations. Share your favorites and find your next binge based on trusted suggestions.',
      tags: ['hidden-gems', 'romance', 'thriller', 'family-drama', 'binge-worthy', 'historical'],
    },
    news: {
      about: 'Stay updated with the latest developments in the Arab drama industry. From production announcements to award ceremonies, we cover it all.',
      tags: ['uae', 'streaming', 'awards', 'production', 'investment', 'co-production'],
    },
    'behind-scenes': {
      about: 'Get exclusive insights into the production process of your favorite Arab dramas. Discover what happens behind the camera.',
      tags: ['filming', 'costume-design', 'set-construction', 'cinematography', 'direction', 'makeup'],
    },
    'ai-dramas': {
      about: 'Explore the exciting world of AI-generated dramas. Discuss tools, techniques, showcase your creations, and connect with fellow AI content creators.',
      tags: ['sora', 'runway', 'elevenlabs', 'character-consistency', 'tutorials', 'showcase', 'monetization', 'prompts'],
    },
  };

  // Check login status before opening create modal
  const handleCreateAction = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    switch (activeTab) {
      case 'reviews':
        setShowWriteReview(true);
        break;
      case 'recommendations':
        setShowCreateRecommendation(true);
        break;
      case 'news':
        setShowSubmitNews(true);
        break;
      case 'behind-scenes':
        setShowShareStory(true);
        break;
      case 'ai-dramas':
        setShowAIDramaPost(true);
        break;
      case 'creators':
        setShowCreatorPost(true);
        break;
      case 'general':
        setShowGeneralDiscussion(true);
        break;
      default:
        setShowCreatePost(true);
    }
  };

  // Check login for ask recommendation
  const handleAskRecommendation = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setShowAskRecommendation(true);
  };

  const getCreateButtonText = () => {
    switch (activeTab) {
      case 'reviews': return 'Write Review';
      case 'recommendations': return 'Share Recommendation';
      case 'news': return 'Submit News';
      case 'behind-scenes': return 'Share Story';
      case 'ai-dramas': return 'New AI Post';
      case 'creators': return 'Spotlight Post';
      default: return 'New Post';
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}></i>
    ));
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handlePollVote = async (postId: string, optionId: string, allowMultiple: boolean, endDate?: string | null) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (isPollExpired(endDate)) {
      showPollNotification('This poll has ended and is no longer accepting votes', 'error');
      return;
    }

    if (allowMultiple) {
      const result = toggleMultiOption(postId, optionId, endDate);
      if (!result.success) {
        if (result.message === 'already_voted') {
          showPollNotification('You have already voted on this poll', 'info');
        } else if (result.message === 'poll_expired') {
          showPollNotification('This poll has ended and is no longer accepting votes', 'error');
        }
      }
    } else {
      const result = await handleSingleVote(postId, optionId, endDate);
      if (!result.success) {
        if (result.message === 'already_voted') {
          showPollNotification('You have already voted on this poll', 'info');
        } else if (result.message === 'poll_expired') {
          showPollNotification('This poll has ended and is no longer accepting votes', 'error');
        } else if (result.message === 'save_failed') {
          showPollNotification('Failed to save your vote. Please try again.', 'error');
        }
      } else {
        showPollNotification('Your vote has been recorded!', 'success');
      }
    }
  };

  const handleSubmitMultipleChoicePoll = async (postId: string, endDate?: string | null) => {
    const result = await submitMultiVote(postId, endDate);
    if (!result.success) {
      if (result.message === 'no_selection') {
        showPollNotification('Please select at least one option', 'error');
      } else if (result.message === 'poll_expired') {
        showPollNotification('This poll has ended and is no longer accepting votes', 'error');
      } else if (result.message === 'save_failed') {
        showPollNotification('Failed to save your vote. Please try again.', 'error');
      }
    } else {
      showPollNotification('Your vote has been submitted!', 'success');
    }
  };

  const showPollNotification = (message: string, type: 'success' | 'error' | 'info') => {
    const colors = {
      success: 'bg-gradient-to-r from-emerald-500 to-teal-500',
      error: 'bg-red-500',
      info: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    };
    const icons = {
      success: 'ri-check-circle-line',
      error: 'ri-error-warning-line',
      info: 'ri-information-line'
    };
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`;
    notification.innerHTML = `<div class="flex items-center"><i class="${icons[type]} mr-2"></i><span>${message}</span></div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('opacity-0', 'translate-x-full');
      setTimeout(() => document.body.contains(notification) && document.body.removeChild(notification), 300);
    }, 3000);
  };

  const stats = categoryStats[activeTab];
  const sidebar = sidebarContent[activeTab];
  const handleLike = (postId: string) => {
    setLikedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleShare = (post: any) => {
    setSharePost(post);
  };

  const handleShareToMessage = (post: any) => {
    const category = discussionCategories.find(c => c.id === post.category);
    setShareToMessageContent({
      type: 'post',
      id: post.id,
      title: post.title,
      description: post.content.substring(0, 120) + (post.content.length > 120 ? '...' : ''),
      image: post.author.avatar,
      url: createDiscussionUrl(post.category, post.id, post.title),
      meta: {
        category: category?.name || post.categoryName,
        categoryColor: category?.bgColor?.replace('bg-', '').includes('blue') ? '#3b82f6' :
          category?.bgColor?.replace('bg-', '').includes('yellow') ? '#eab308' :
          category?.bgColor?.replace('bg-', '').includes('purple') ? '#a855f7' :
          category?.bgColor?.replace('bg-', '').includes('green') ? '#22c55e' :
          category?.bgColor?.replace('bg-', '').includes('red') ? '#ef4444' :
          category?.bgColor?.replace('bg-', '').includes('indigo') ? '#6366f1' : '#6b7280',
        author: post.author.username
      }
    });
    setShowShareToMessage(true);
  };

  const handleSendSharedContent = async (conversationId: string, content: SharedContent) => {
    const encoded = encodeSharedContent(content);
    await sendMessage(conversationId, encoded);
  };

  const handleStartNewConvWithContent = async (userId: string, content: SharedContent) => {
    const convId = await startConversation(userId);
    if (convId) {
      const encoded = encodeSharedContent(content);
      await sendMessage(convId, encoded);
      await fetchConversations();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section with Dynamic Color */}
      <div className={`${currentCategory.bgColor} text-white py-12`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
              <i className={`${currentCategory.icon} text-white text-2xl`}></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold">{currentCategory.name}</h1>
              <p className="text-white/80 mt-2">
                {activeTab === 'general' && 'Open discussions about Arab dramas and community topics'}
                {activeTab === 'reviews' && 'Share detailed reviews, ratings, and critiques of Arab short dramas'}
                {activeTab === 'creators' && 'Discuss actors, directors, producers, and other creative professionals'}
                {activeTab === 'recommendations' && 'Help others discover new dramas through curated recommendations'}
                {activeTab === 'news' && 'Stay updated with the latest industry news and announcements'}
                {activeTab === 'behind-scenes' && 'Get behind-the-scenes insights into drama productions'}
                {activeTab === 'ai-dramas' && 'Explore AI-generated drama content, tools, and tutorials'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 text-sm text-white/80">
            <span className="flex items-center">
              <i className="ri-message-3-line mr-1"></i>
              {stats.posts.toLocaleString()} posts
            </span>
            <span className="flex items-center">
              <i className="ri-group-line mr-1"></i>
              {stats.members.toLocaleString()} members
            </span>
            <span className="flex items-center">
              <i className="ri-star-line mr-1"></i>
              {stats.extra}
            </span>
          </div>
        </div>
      </div>

      {/* Horizontal Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide -mb-px">
            {discussionCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleTabChange(category.id)}
                className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors cursor-pointer ${activeTab === category.id
                  ? `border-current ${category.color}`
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              >
                <i className={`${category.icon} ${activeTab === category.id ? category.color : ''}`}></i>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Controls */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-4">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                  >
                    <option value="latest">Latest Posts</option>
                    <option value="popular">Most Popular</option>
                    <option value="discussed">Most Discussed</option>
                    <option value="views">Most Viewed</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search posts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm"
                    />
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  </div>
                  
                  <button 
                    onClick={handleCreateAction}
                    className={`${currentCategory.bgColor} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer`}
                  >
                    <i className="ri-add-line mr-1"></i>
                    {getCreateButtonText()}
                  </button>
                </div>
              </div>
            </div>

            {/* Posts List */}
            {filteredPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
                <p className="text-gray-600">Try adjusting your search terms or create a new post.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPosts.map((post) => (
                  <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    {/* Breaking News Badge */}
                    {post.isBreaking && (
                      <div className="flex items-center text-red-600 text-sm font-medium mb-3">
                        <i className="ri-alarm-warning-line mr-1"></i>
                        Breaking News
                      </div>
                    )}
                    
                    {/* Pinned Badge */}
                    {post.isPinned && (
                      <div className="flex items-center text-amber-600 text-sm font-medium mb-3">
                        <i className="ri-pushpin-fill mr-1"></i>
                        Pinned
                      </div>
                    )}
                    
                    <div className="flex items-start space-x-4">
                      <Link to={createUserProfileUrl(post.author.id, post.author.username)}>
                        <img
                          src={post.author.avatar}
                          alt={post.author.username}
                          className="w-12 h-12 rounded-full object-cover hover:ring-2 hover:ring-amber-300 transition-all cursor-pointer"
                        />
                      </Link>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Link to={createUserProfileUrl(post.author.id, post.author.username)} className="font-semibold text-gray-900 hover:text-amber-600 transition-colors cursor-pointer">
                            {post.author.username}
                          </Link>
                          <span className="text-gray-500 text-sm">&bull;</span>
                          <span className="text-gray-500 text-sm">{formatTimeAgo(post.createdAt)}</span>
                          {post.source && (
                            <>
                              <span className="text-gray-500 text-sm">•</span>
                              <span className="text-gray-500 text-sm">{post.source}</span>
                            </>
                          )}
                        </div>
                        
                        {/* Category-specific metadata */}
                        <div className="flex items-center space-x-3 mb-3">
                          {post.drama && (
                            <span className="text-sm font-medium text-gray-700">
                              {activeTab === 'reviews' ? 'Reviewing: ' : 'From: '}{post.drama}
                            </span>
                          )}
                          {post.rating && (
                            <div className="flex items-center space-x-1">
                              {renderStars(post.rating)}
                              <span className="text-sm text-gray-600 ml-1">({post.rating}/5)</span>
                            </div>
                          )}
                        </div>
                        
                        <Link 
                          to={createDiscussionUrl(post.category, post.id, post.title)}
                          className="block"
                        >
                          <h2 className={`text-lg font-semibold text-gray-900 mb-2 hover:${currentCategory.color} cursor-pointer transition-colors`}>
                            {post.title}
                          </h2>
                        </Link>
                        
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {post.content}
                        </p>
                        
                        {/* Recommended Dramas */}
                        {post.recommendedDramas && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Recommended Dramas:</h4>
                            <div className="flex flex-wrap gap-2">
                              {post.recommendedDramas.map((drama: string, index: number) => (
                                <span key={index} className="px-2 py-1 bg-white text-gray-700 text-xs rounded border hover:bg-green-50 hover:border-green-300 cursor-pointer transition-colors">
                                  {drama}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Poll Display */}
                        {post.metadata?.poll && (() => {
                          const pollExpired = isPollExpired(post.metadata.poll.endDate);
                          const rtData = realtimeVoteCounts[post.id];
                          return (
                          <div className={`bg-gradient-to-br ${pollExpired ? 'from-gray-50 to-gray-100 border-gray-300' : 'from-blue-50 to-cyan-50 border-blue-200'} rounded-xl p-5 mb-4 border`}>
                            <div className="flex items-center justify-between mb-4">
                              <h4 className={`text-sm font-semibold ${pollExpired ? 'text-gray-600' : 'text-blue-800'} flex items-center gap-2`}>
                                <i className="ri-bar-chart-horizontal-line"></i>
                                Community Poll
                                {rtData && (
                                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-medium animate-pulse">
                                    <i className="ri-live-line mr-0.5"></i>
                                    LIVE
                                  </span>
                                )}
                                {pollExpired && (
                                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-xs font-medium ml-1">
                                    <i className="ri-lock-line mr-1"></i>
                                    Ended
                                  </span>
                                )}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-blue-600">
                                {!user && !pollExpired && (
                                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                    <i className="ri-lock-line mr-1"></i>
                                    Sign in to vote
                                  </span>
                                )}
                                {post.metadata.poll.allowMultiple && (
                                  <span className="bg-blue-100 px-2 py-0.5 rounded">
                                    <i className="ri-checkbox-multiple-line mr-1"></i>
                                    Multiple choice
                                  </span>
                                )}
                                {post.metadata.poll.endDate && (
                                  <span className={`px-2 py-0.5 rounded ${pollExpired ? 'bg-red-100 text-red-600' : 'bg-blue-100'}`}>
                                    <i className={`${pollExpired ? 'ri-time-fill' : 'ri-time-line'} mr-1`}></i>
                                    {pollExpired 
                                      ? `Ended ${new Date(post.metadata.poll.endDate).toLocaleDateString()}`
                                      : `Ends ${new Date(post.metadata.poll.endDate).toLocaleDateString()}`
                                    }
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="space-y-2">
                              {post.metadata.poll.options.map((option: { id: string; text: string; votes: number }) => {
                                const liveOptionVotes = rtData ? (rtData.optionCounts[option.id] || 0) : option.votes;
                                const liveTotalVotes = rtData ? rtData.totalVotes : (post.metadata.poll.totalVotes || post.metadata.poll.options.reduce((sum: number, opt: any) => sum + opt.votes, 0));
                                const displayVotes = rtData ? liveOptionVotes : liveOptionVotes + (votedPolls[post.id]?.includes(option.id) ? 1 : 0);
                                const displayTotal = rtData ? liveTotalVotes : liveTotalVotes + (votedPolls[post.id]?.length ? 1 : 0);
                                const percentage = displayTotal > 0 ? Math.round((displayVotes / displayTotal) * 100) : 0;
                                const isVoted = votedPolls[post.id]?.includes(option.id);
                                const hasVoted = (votedPolls[post.id]?.length || 0) > 0;
                                const isSubmitted = submittedPolls.has(post.id);
                                const showResults = post.metadata.poll.showResults || hasVoted || pollExpired;
                                const isLockedMultiple = post.metadata.poll.allowMultiple && isSubmitted;
                                const isDisabled = isSubmitted || isLockedMultiple || pollExpired;

                                return (
                                  <button
                                    key={option.id}
                                    onClick={() => handlePollVote(post.id, option.id, post.metadata.poll.allowMultiple, post.metadata.poll.endDate)}
                                    disabled={isDisabled}
                                    className={`relative w-full bg-white rounded-lg border-2 transition-all group overflow-hidden text-left ${
                                      isDisabled
                                        ? 'cursor-default opacity-90'
                                        : 'cursor-pointer'
                                    } ${
                                      isVoted 
                                        ? 'border-blue-500 ring-2 ring-blue-200' 
                                        : isDisabled
                                          ? 'border-gray-200'
                                          : 'border-blue-200 hover:border-blue-400'
                                    }`}
                                  >
                                    {/* Progress bar background */}
                                    {showResults && (
                                      <div 
                                        className={`absolute inset-0 transition-all duration-700 ease-out ${isVoted ? 'bg-blue-100' : 'bg-gray-100'}`}
                                        style={{ width: `${percentage}%` }}
                                      ></div>
                                    )}
                                    
                                    <div className="relative flex items-center justify-between p-3">
                                      <div className="flex items-center">
                                        <div className={`w-5 h-5 ${post.metadata.poll.allowMultiple ? 'rounded' : 'rounded-full'} border-2 mr-3 flex items-center justify-center transition-colors ${
                                      isVoted ? 'border-blue-500 bg-blue-500' : isDisabled ? 'border-gray-300' : 'border-blue-300 group-hover:border-blue-500'
                                    }`}>
                                          {isVoted && (
                                            <i className="ri-check-line text-white text-xs"></i>
                                          )}
                                        </div>
                                        <span className={`font-medium ${isVoted ? 'text-blue-800' : 'text-gray-800'}`}>
                                          {option.text}
                                        </span>
                                      </div>
                                      
                                      {showResults && (
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className={`font-semibold ${isVoted ? 'text-blue-600' : 'text-gray-600'}`}>
                                            {percentage}%
                                          </span>
                                          <span className="text-gray-400 text-xs">
                                            ({displayVotes})
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Submit button for multiple choice polls */}
                            {post.metadata.poll.allowMultiple && !submittedPolls.has(post.id) && !pollExpired && (votedPolls[post.id]?.length || 0) > 0 && (
                              <div className="mt-3">
                                <button
                                  onClick={() => handleSubmitMultipleChoicePoll(post.id, post.metadata.poll.endDate)}
                                  className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center justify-center gap-2"
                                >
                                  <i className="ri-check-double-line"></i>
                                  Submit Vote ({votedPolls[post.id]?.length || 0} selected)
                                </button>
                              </div>
                            )}

                            {/* Poll Footer with Analytics & Share */}
                            <div className="mt-4 flex items-center justify-between">
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1 tabular-nums">
                                  <i className="ri-group-line"></i>
                                  {rtData 
                                    ? rtData.totalVotes 
                                    : (post.metadata.poll.totalVotes || post.metadata.poll.options.reduce((sum: number, opt: any) => sum + opt.votes, 0)) + (votedPolls[post.id]?.length ? 1 : 0)
                                  } votes
                                </span>
                                {!user && !pollExpired && (
                                  <button
                                    onClick={() => setShowAuthModal(true)}
                                    className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-login-box-line mr-1"></i>
                                    Sign in to vote
                                  </button>
                                )}
                                {pollExpired && (
                                  <span className="text-red-500 flex items-center gap-1 font-medium">
                                    <i className="ri-calendar-close-line"></i>
                                    Voting closed
                                  </span>
                                )}
                                {!pollExpired && !post.metadata.poll.showResults && !(votedPolls[post.id]?.length) && user && (
                                  <span className="text-blue-600">
                                    <i className="ri-eye-off-line mr-1"></i>
                                    Vote to see results
                                  </span>
                                )}
                                {submittedPolls.has(post.id) && (
                                  <span className="text-green-600 flex items-center gap-1">
                                    <i className="ri-check-double-line"></i>
                                    Vote submitted
                                  </span>
                                )}
                                {!pollExpired && (votedPolls[post.id]?.length || 0) > 0 && !post.metadata.poll.allowMultiple && !submittedPolls.has(post.id) && (
                                  <span className="text-amber-600 flex items-center gap-1">
                                    <i className="ri-information-line"></i>
                                    Click submit to confirm
                                  </span>
                                )}
                                {!pollExpired && (votedPolls[post.id]?.length || 0) > 0 && post.metadata.poll.allowMultiple && !submittedPolls.has(post.id) && (
                                  <span className="text-amber-600 flex items-center gap-1">
                                    <i className="ri-information-line"></i>
                                    Click submit to confirm
                                  </span>
                                )}
                              </div>
                              
                              {/* Analytics & Share Buttons */}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    const rtd = realtimeVoteCounts[post.id];
                                    setSelectedPollData({
                                      pollTitle: post.title,
                                      pollId: post.id,
                                      options: post.metadata.poll.options.map((opt: any) => ({
                                        ...opt,
                                        votes: rtd ? (rtd.optionCounts[opt.id] || 0) : opt.votes
                                      })),
                                      totalVotes: rtd ? rtd.totalVotes : (post.metadata.poll.totalVotes || post.metadata.poll.options.reduce((sum: number, opt: any) => sum + opt.votes, 0)),
                                      createdAt: post.createdAt,
                                      endDate: post.metadata.poll.endDate,
                                      allowMultiple: post.metadata.poll.allowMultiple
                                    });
                                    setShowPollAnalytics(true);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                                  title="View poll analytics"
                                >
                                  <i className="ri-bar-chart-grouped-line"></i>
                                  <span className="hidden sm:inline">Analytics</span>
                                </button>
                                <button
                                  onClick={() => {
                                    const pollUrl = `${window.location.origin}/forum?poll=${post.id}`;
                                    const rtd = realtimeVoteCounts[post.id];
                                    const options = post.metadata.poll.options.map((opt: any) => ({
                                      ...opt,
                                      votes: rtd ? (rtd.optionCounts[opt.id] || 0) : opt.votes
                                    }));
                                    const leader = [...options].sort((a: any, b: any) => b.votes - a.votes)[0];
                                    const totalVotes = rtd ? rtd.totalVotes : (post.metadata.poll.totalVotes || post.metadata.poll.options.reduce((sum: number, opt: any) => sum + opt.votes, 0));
                                    const leaderPercent = totalVotes > 0 ? Math.round((leader.votes / totalVotes) * 100) : 0;
                                    
                                    setSharePollResults({
                                      title: `📊 Poll: ${post.title}`,
                                      text: `🏆 Leading: ${leader.text} (${leaderPercent}%)\n👥 ${totalVotes} votes`,
                                      url: pollUrl
                                    });
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
                                  title="Share poll results"
                                >
                                  <i className="ri-share-line"></i>
                                  <span className="hidden sm:inline">Share</span>
                                </button>
                              </div>
                            </div>
                          </div>
                          );
                        })()}

                        {/* Tags */}
                        {post.tags && (
                          <div className="flex items-center space-x-2 mb-4">
                            {post.tags.slice(0, 4).map((tag: string, index: number) => (
                              <span key={index} className={`px-2 py-1 ${currentCategory.bgColor}/10 ${currentCategory.color} text-xs rounded-full`}>
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            <button 
                              onClick={() => handleLike(post.id)}
                              className={`flex items-center gap-1 ${post.isLiked ? 'text-orange-500' : 'hover:text-orange-500'} transition-colors cursor-pointer`}
                            >
                              <i className={post.isLiked ? "ri-heart-fill" : "ri-heart-line"}></i>
                              <span>{post.likes}</span>
                            </button>
                            <button 
                              onClick={() => handleNavigate(`/forum/${post.category}/detail/${post.id}`)}
                              className="flex items-center gap-1 hover:text-orange-500 transition-colors cursor-pointer"
                            >
                              <i className="ri-message-3-line"></i>
                              <span>{post.commentCount}</span>
                            </button>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              <span>{post.views}</span>
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleShareToMessage(post)}
                              className="text-purple-500 hover:text-purple-700 transition-colors cursor-pointer"
                              title="Send in message"
                            >
                              <i className="ri-message-3-line"></i>
                            </button>
                            <button 
                              onClick={() => handleShare(post)}
                              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                              title="Share"
                            >
                              <i className="ri-share-line"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80">
            <div className="space-y-6">
              {/* Banner Widget */}
              <ForumBannerWidget activeTab={activeTab} />

              {/* Category Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">About {currentCategory.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{sidebar.about}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Posts:</span>
                    <span className="font-medium">{stats.posts.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Members:</span>
                    <span className="font-medium">{stats.members.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Ask for Recommendations */}
              {activeTab === 'recommendations' && (
                <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-lg p-6 text-white">
                  <h3 className="font-semibold mb-2">Need Recommendations?</h3>
                  <p className="text-sm text-green-100 mb-4">
                    Can't find what you're looking for? Ask the community for personalized suggestions!
                  </p>
                  <button 
                    onClick={handleAskRecommendation}
                    className="bg-white text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Ask for Recommendations
                  </button>
                </div>
              )}

              {/* AI Drama Creator Apply CTA - shown on ai-dramas tab */}
              {activeTab === 'ai-dramas' && (
                <div className="bg-gradient-to-br from-orange-500 via-rose-500 to-amber-500 rounded-lg p-6 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <i className="ri-robot-2-line text-xl"></i>
                      </div>
                      <h3 className="font-bold text-lg">Become a Creator</h3>
                    </div>
                    <p className="text-sm text-white/80 mb-4 leading-relaxed">
                      Apply to become a verified AI Drama Creator. Get a creator badge, featured placement, and access to exclusive resources.
                    </p>
                    <button
                      onClick={() => setShowAIDramaApply(true)}
                      className="w-full py-2.5 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap text-sm shadow-lg"
                    >
                      <i className="ri-send-plane-line mr-2"></i>
                      Apply Now
                    </button>
                  </div>
                </div>
              )}

              {/* Popular Tags */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {sidebar.tags.map((tag) => (
                    <button
                      key={tag}
                      className={`px-3 py-1 ${currentCategory.bgColor}/10 ${currentCategory.color} text-xs rounded-full hover:opacity-80 transition-colors cursor-pointer`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Categories */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Other Categories</h3>
                <div className="space-y-3">
                  {discussionCategories.filter(c => c.id !== activeTab).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleTabChange(category.id)}
                      className="flex items-center space-x-3 text-sm text-gray-600 hover:text-gray-900 transition-colors w-full cursor-pointer"
                    >
                      <i className={`${category.icon} ${category.color}`}></i>
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />

      {/* Modals */}
      {showCreatePost && (
        <CreatePostModal
          isOpen={showCreatePost}
          onClose={() => setShowCreatePost(false)}
          defaultCategory={currentCategory.name}
          categories={discussionCategories.map(c => ({ id: c.id, name: c.name }))}
          onPostCreated={() => setShowCreatePost(false)}
        />
      )}

      <WriteReviewModal
        isOpen={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        onSuccess={() => setShowWriteReview(false)}
      />

      <CreateRecommendationModal
        isOpen={showCreateRecommendation}
        onClose={() => setShowCreateRecommendation(false)}
      />

      <AskRecommendationModal
        isOpen={showAskRecommendation}
        onClose={() => setShowAskRecommendation(false)}
      />

      <SubmitNewsModal
        isOpen={showSubmitNews}
        onClose={() => setShowSubmitNews(false)}
      />

      <ShareStoryModal
        isOpen={showShareStory}
        onClose={() => setShowShareStory(false)}
      />

      <CreateAIDramaPostModal
        isOpen={showAIDramaPost}
        onClose={() => setShowAIDramaPost(false)}
      />

      <CreateCreatorPostModal
        isOpen={showCreatorPost}
        onClose={() => setShowCreatorPost(false)}
      />

      <GeneralDiscussionModal
        isOpen={showGeneralDiscussion}
        onClose={() => setShowGeneralDiscussion(false)}
      />

      {sharePost && (
        <ShareModal
          isOpen={!!sharePost}
          onClose={() => setSharePost(null)}
          title={sharePost.title}
          url={`${window.location.origin}${createDiscussionUrl(sharePost.category, sharePost.id, sharePost.title)}`}
          onShareToMessage={() => {
            handleShareToMessage(sharePost);
            setSharePost(null);
          }}
        />
      )}

      <ShareToMessageModal
        isOpen={showShareToMessage}
        onClose={() => setShowShareToMessage(false)}
        content={shareToMessageContent}
        onSend={handleSendSharedContent}
        onStartNewConversation={handleStartNewConvWithContent}
      />

      {/* Poll Results Analytics Modal */}
      {showPollAnalytics && selectedPollData && (
        <PollResultsAnalytics
          isOpen={showPollAnalytics}
          onClose={() => {
            setShowPollAnalytics(false);
            setSelectedPollData(null);
          }}
          pollTitle={selectedPollData.pollTitle}
          pollId={selectedPollData.pollId}
          options={selectedPollData.options}
          totalVotes={selectedPollData.totalVotes}
          createdAt={selectedPollData.createdAt}
          endDate={selectedPollData.endDate}
          allowMultiple={selectedPollData.allowMultiple}
        />
      )}

      {/* Poll Results Share Modal */}
      {sharePollResults && (
        <ShareModal
          isOpen={!!sharePollResults}
          onClose={() => setSharePollResults(null)}
          title={sharePollResults.title}
          url={sharePollResults.url}
        />
      )}

      {/* AI Drama Apply Modal */}
      <AIDramaApplyModal
        isOpen={showAIDramaApply}
        onClose={() => setShowAIDramaApply(false)}
      />
    </div>
  );
}